import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { X, Snowflake, Save, CheckCircle2, Printer, FileSpreadsheet, XCircle, ClipboardCheck } from "lucide-react";
import { NeuButton } from "../../components/neu/Neu";
import { getCount, freezeCount, patchCountLines, submitCount, cancelCount, printCountLink } from "../../api/counts";
import { CountSheet } from "./CountSheet";
import client from "../../api/client";
import { localName } from "../../lib/localName";

const STATE_COLORS = { draft: "amber", frozen: "sky", posted: "emerald", cancelled: "rose" };

// تفاصيل الجرد (شاشة كاملة): ترويسة ثابتة + KPIs + جدول قابل للتمرير.
export const CountDetail = ({ countId, onBack }) => {
  const { t, i18n } = useTranslation();
  const [doc, setDoc] = useState(null);
  const [lines, setLines] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => getCount(countId).then((d) => { setDoc(d); setLines(d.lines || []); });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [countId]);

  if (!doc) return <div className="fixed inset-0 flex items-center justify-center bg-background">
    <p className="text-sm text-muted-foreground">…</p></div>;
  const editable = ["draft", "frozen"].includes(doc.state);
  const num = (n) => new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 }).format(n || 0);

  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(t(ok)); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail?.code || t("errors.generic")); }
    finally { setBusy(false); }
  };
  const save = () => act(() => patchCountLines(countId,
    lines.map((l) => ({ item_id: l.item_id, counted_qty: l.counted_qty ?? null, note: l.note || "" }))),
    "warehouse.count.saved");
  const freeze = () => act(() => freezeCount(countId), "warehouse.count.frozen");
  const submit = () => window.confirm(t("warehouse.count.submitConfirm"))
    && act(() => submitCount(countId, false), "warehouse.count.posted");
  const cancel = () => { const r = window.prompt(t("warehouse.count.cancelReason"));
    if (r) act(() => cancelCount(countId, r), "warehouse.count.cancelled"); };
  const download = async () => {
    const r = await client.get(`/warehouse/counts/${countId}/export.xlsx`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([r.data]));
    const a = document.createElement("a"); a.href = url; a.download = `${doc.number}.xlsx`; a.click();
    URL.revokeObjectURL(url);
  };
  const printOfficial = async () => {
    const r = await printCountLink(countId); window.open(r.url, "_blank");
  };

  const tot = doc.totals || {};
  return (
    <div className="fixed inset-0 z-[80] flex flex-col bg-background" data-testid="count-detail">
      <div className="flex flex-shrink-0 flex-col gap-3 bg-gradient-to-r from-teal-600 to-cyan-600 px-6 pb-4 pt-[max(1rem,calc(env(safe-area-inset-top)+0.75rem))] text-white shadow-xl">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <ClipboardCheck className="h-6 w-6 flex-shrink-0" />
            <div className="min-w-0"><p className="num font-mono text-lg font-black">{doc.number}</p>
              <p className="truncate text-[11px] opacity-80">{localName(doc.warehouse)} · {doc.date} · {t(`warehouse.count.types.${doc.count_type}`)}</p></div>
            <span className={`flex-shrink-0 rounded-full bg-${STATE_COLORS[doc.state]}-500 px-3 py-1 text-[11px] font-bold text-white`}>
              {t(`warehouse.count.states.${doc.state}`)}</span>
          </div>
          <button onClick={onBack} data-testid="cnt-close"
                  className="flex-shrink-0 rounded-full bg-white/15 p-2 transition hover:bg-white/25">
            <X className="h-6 w-6" /></button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {editable && <NeuButton onClick={save} disabled={busy} data-testid="cnt-save-btn" className="bg-white/15 text-white hover:bg-white/25">
            <Save className="inline h-4 w-4" /> {t("warehouse.count.save")}</NeuButton>}
          {doc.state === "draft" && <NeuButton onClick={freeze} disabled={busy} data-testid="cnt-freeze"
            className="bg-white/15 text-white hover:bg-white/25"><Snowflake className="inline h-4 w-4" /> {t("warehouse.count.freeze")}</NeuButton>}
          {editable && <NeuButton onClick={submit} disabled={busy} data-testid="cnt-submit" className="bg-emerald-500 text-white hover:bg-emerald-600">
            <CheckCircle2 className="inline h-4 w-4" /> {t("warehouse.count.submit")}</NeuButton>}
          <NeuButton onClick={download} data-testid="cnt-export" className="bg-white/15 text-white hover:bg-white/25">
            <FileSpreadsheet className="inline h-4 w-4" /> {t("warehouse.actions.export")}</NeuButton>
          {doc.state === "posted" && <NeuButton onClick={printOfficial} data-testid="cnt-print" className="bg-slate-800 text-white hover:bg-slate-900">
            <Printer className="inline h-4 w-4" /> {t("warehouse.count.printOfficial")}</NeuButton>}
          {editable && <NeuButton onClick={cancel} className="bg-rose-500 text-white hover:bg-rose-600" data-testid="cnt-cancel">
            <XCircle className="inline h-4 w-4" /> {t("warehouse.count.cancelCount")}</NeuButton>}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-5 sm:px-8">
        <div className="mx-auto grid max-w-7xl gap-4">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
            {[["items_count", tot.items_count], ["counted", tot.counted], ["variances", tot.variances],
              ["shortage_qty", num(tot.shortage_qty)], ["overage_qty", num(tot.overage_qty)]].map(([k, v]) => (
              <div key={k} className="rounded-2xl shadow-neu-in p-3 text-center">
                <p className="text-[10px] text-muted-foreground">{t(`warehouse.count.kpi.${k}`)}</p>
                <p className="num font-mono text-lg font-black">{v ?? 0}</p></div>))}
          </div>
          <CountSheet lines={lines} onChange={setLines} readOnly={!editable} />
        </div>
      </div>
    </div>
  );
};
